import { Typography } from '@material-ui/core';
import { Chip, Grid, Tab, Tabs } from '@mui/material';
import { useEffect, useState } from 'react';
import TripWizardRoundTripSwitch from './ruond-trip-switch';
import FilterableTable from '../../../components/FilterableTable/FilterableTable';
import { CustomerLocationOrder, Vehicle, ZoneRoutingInfo } from '../../../store/config/types';
import { convertConstantLiteralToNormal } from '../../../utils/string.helpers';
import CourierSelector from '../../../components/TripsDnd/CourierSelector';
import tripsService from '../../../services/trips.service';

interface TripWizardZoneVehicleSelectorProps {
  routingAttributes: any;
}

export default function TripWizardZoneVehicleSelector({ routingAttributes }: TripWizardZoneVehicleSelectorProps) {
  const [currentZone, setCurrentZone] = useState(
    routingAttributes?.current?.zones?.length && routingAttributes.current.zones[0].deliveryZoneId,
  );
  const zones: any[] = routingAttributes?.current?.zones;
  useEffect(() => {
    const deliveryOrders: CustomerLocationOrder[] = routingAttributes.current.deliveryOrdersOfDate;
    const selectedDeliveryOrders: number[] = routingAttributes.current.selectedDeliveryOrders;
    const zonesInfo: { deliveryZoneId: number; warehouseId: number; requiredCapacity: number }[] = [];
    selectedDeliveryOrders.forEach((dIndex) => {
      const zfIndex = zonesInfo.findIndex(
        (zf) =>
          zf.deliveryZoneId == deliveryOrders[dIndex].additionalData?.zoneId &&
          zf.warehouseId == deliveryOrders[dIndex].warehouseId,
      );
      const requiredCapacity = Math.ceil(
        deliveryOrders[dIndex]?.packageGroups.reduce((total, pg) => total + (pg.packageCount ?? 0), 0),
      );
      if (zfIndex < 0) {
        zonesInfo.push({
          deliveryZoneId: deliveryOrders[dIndex].additionalData?.zoneId,
          requiredCapacity,
          warehouseId: deliveryOrders[dIndex].warehouseId,
        });
      } else {
        zonesInfo[zfIndex].requiredCapacity += requiredCapacity;
      }
    });
    if (zonesInfo.length) {
      setCurrentZone(null);
      tripsService.allocateVehiclesPerZone(zonesInfo, routingAttributes?.current?.tripDate).then((zoneData: any[]) => {
        const zoneAllVehicles: { zoneId: number; vehicleIds: number[] }[] = [];
        zoneData.forEach((zData) => {
          const zvIndex = zoneAllVehicles.findIndex((z) => z.zoneId == zData.deliveryZoneId);
          if (zvIndex < 0) {
            zoneAllVehicles.push({ zoneId: zData.deliveryZoneId, vehicleIds: zData.vehicleIds });
          } else {
            zoneAllVehicles[zvIndex].vehicleIds.push(...zData.vehicleIds);
          }
        });
        zoneAllVehicles.forEach((zData) => {
          handleVehicleSelection(zData.vehicleIds, zData.zoneId);
        });

        setCurrentZone(zoneData[0].deliveryZoneId);
      });
    }
  }, [zones]);

  function changeCourierOfVehicle(vehicleId: number, courierId: number | undefined): boolean {
    if (!routingAttributes?.current?.vehicles) false;
    const vehicles: Vehicle[] = routingAttributes?.current?.vehicles;
    const vehicleIndex = vehicles.findIndex((v) => v.vehicleId === vehicleId);
    if (vehicleIndex >= 0) {
      const updatedVehicles = [...vehicles];
      updatedVehicles[vehicleIndex].courierId = courierId;
      routingAttributes.current.vehicles = updatedVehicles;
      return true;
    }
    return false;
  }
  function handleVehicleSelection(ids: any[], zoneId?: number): void {
    console.log('selection of vehicles', ids.join(', '));
    if (!routingAttributes?.current?.zonesInfo) false;
    const zonesInfo: { [key: number]: ZoneRoutingInfo } = routingAttributes.current.zonesInfo;
    if (zonesInfo[zoneId ?? currentZone]) {
      zonesInfo[zoneId ?? currentZone].vehicleIds = ids;
    }
    routingAttributes.current.zonesInfo = { ...zonesInfo };
  }

  return (
    <Grid container flexDirection="column">
      <Grid item p={1}>
        <Typography variant="subtitle1">All Vehicles are available, you may manage manually</Typography>
      </Grid>
      <Grid item p={1} style={{ width: '100%' }}>
        {zones?.length > 1 && (
          <Tabs
            value={currentZone}
            onChange={(e, newValue) => setCurrentZone(newValue)}
            variant="scrollable"
            scrollButtons="auto"
          >
            {routingAttributes?.current?.zones.map((zone: any, zoneIndex: number) => (
              <Tab value={zone.deliveryZoneId} label={zone.caption} key={zoneIndex} />
            ))}
          </Tabs>
        )}
      </Grid>

      <Grid item p={1}>
        <TripWizardRoundTripSwitch currentZone={currentZone} routingAttributes={routingAttributes} />
      </Grid>
      <Grid item flexGrow={1} position="relative">
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, overflow: 'scroll' }}>
          <FilterableTable
            columns={[
              { field: 'displayName', caption: 'Name', sortable: true, filterType: 'string' },
              { field: 'capacity', caption: 'Capacity', sortable: true, filterType: 'string' },
              {
                field: 'status',
                caption: 'Status',
                sortable: true,
                filterType: 'values',
                render: (row) => {
                  return (
                    <Chip
                      label={row.status}
                      size="small"
                      color={row.status === 'Available' ? 'success' : 'default'}
                      variant="outlined"
                    />
                  );
                },
              },
              { field: 'licensePlate', caption: 'License Plate', sortable: true, filterType: 'string' },
              { field: 'vehicleType', caption: 'Type', sortable: true, filterType: 'values' },
              { field: 'model', caption: 'Model', sortable: true, filterType: 'string' },
              {
                field: 'courier',
                caption: 'Courier',
                sortable: true,
                filterType: 'string',
                render: (row: any) => {
                  const vehicle = routingAttributes?.current?.vehicles?.find((v: Vehicle) => v.vehicleId == row.id);
                  return (
                    <CourierSelector
                      couriers={routingAttributes?.current?.couriers ?? []}
                      index={vehicle.vehicleId ?? 0}
                      courierId={vehicle.courierId}
                      onChange={(vehicleId: number, courierId: number | undefined) => {
                        return changeCourierOfVehicle(vehicleId, courierId);
                      }}
                    />
                  );
                },
              },
            ]}
            data={routingAttributes?.current?.vehicles?.map((vehicle: Vehicle) => {
              return {
                id: vehicle.vehicleId ?? 0,
                displayName: vehicle.displayName ?? '',
                status: convertConstantLiteralToNormal(vehicle.status ?? ''),
                licensePlate: vehicle.licensePlate ?? '',
                vehicleType: vehicle.vehicleType ?? '',
                model: vehicle.model ?? '',
                capacity: vehicle.capacity ?? '',
                courier: vehicle.courier ? `${vehicle.courier.firstName} ${vehicle.courier.lastName}` : '',
              };
            })}
            selectable
            pinSelectedRows
            primaryKey="id"
            selectedIds={routingAttributes?.current?.zonesInfo[currentZone]?.vehicleIds}
            onSelectionChange={handleVehicleSelection}
          />
        </div>
      </Grid>
    </Grid>
  );
}
